import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { api, setAdminToken, type Project, type ReportDetail } from "../lib/api";

/**
 * withToast decorates a mutation's onSuccess / onError to surface a toast
 * alongside any other side effects (cache invalidation, optimistic
 * updates, redirects). The success / error callbacks accept either a
 * string or a function of the data/error. Pass an `onSuccess` or
 * `onError` alongside the toast config to run additional side effects
 * (like local state updates) after the toast.
 */
function withToast<TData, TError, TVariables>(
  options: {
    success?: string | ((data: TData, variables: TVariables) => string);
    error?: string | ((error: TError) => string);
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: TError) => void;
  },
  base: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: TError) => void;
  }
) {
  return {
    onSuccess: (data: TData, variables: TVariables) => {
      if (options.success) {
        const msg = typeof options.success === "function"
          ? options.success(data, variables)
          : options.success;
        if (msg) toast.success(msg);
      }
      base.onSuccess?.(data, variables);
      options.onSuccess?.(data, variables);
    },
    onError: (error: TError) => {
      if (options.error) {
        const msg = typeof options.error === "function"
          ? options.error(error)
          : options.error;
        if (msg) toast.error(msg);
      }
      base.onError?.(error);
      options.onError?.(error);
    },
  };
}

export const queryKeys = {
  projects: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  reports: (projectId: string) => ["projects", projectId, "reports"] as const,
  report: (projectId: string, reportId: string) =>
    ["projects", projectId, "reports", reportId] as const,
  apiKeys: (projectId: string) => ["projects", projectId, "api-keys"] as const,
  webhooks: (projectId: string) => ["projects", projectId, "webhooks"] as const,
  mcpTokens: ["mcp-tokens"] as const,
};
// Note: a per-project `project(id)` key is intentionally omitted. The
// backend does not expose GET /api/admin/v1/projects/:id; useProject
// derives the project from the cached list.

export function useProjects(options?: { enabled?: boolean }): UseQueryResult<Awaited<ReturnType<typeof api.listProjects>>> {
  return useQuery({
    queryKey: queryKeys.projects,
    queryFn: () => api.listProjects(),
    enabled: options?.enabled ?? true,
    // The project list rarely changes during a session and is rendered
    // by the sidebar on every page. 30s of staleness avoids the
    // loading flicker when the user navigates between tabs.
    staleTime: 30_000,
  });
}

export function useProject(id: string) {
  const projects = useProjects();
  return {
    ...projects,
    project: projects.data?.projects.find((p) => p.id === id) ?? null,
  };
}

export function useReports(projectId: string) {
  return useQuery({
    queryKey: queryKeys.reports(projectId),
    queryFn: () => api.listReports(projectId),
    enabled: !!projectId,
  });
}

export function useReport(projectId: string, reportId: string) {
  return useQuery({
    queryKey: queryKeys.report(projectId, reportId),
    queryFn: () => api.getReport(projectId, reportId),
    enabled: !!projectId && !!reportId,
  });
}

export function useApiKeys(projectId: string) {
  return useQuery({
    queryKey: queryKeys.apiKeys(projectId),
    queryFn: () => api.listApiKeys(projectId),
    enabled: !!projectId,
  });
}

export function useCreateProject(options?: {
  success?: string | ((project: Project) => string);
  error?: string | ((error: Error) => string);
}): UseMutationResult<
  Awaited<ReturnType<typeof api.createProject>>,
  Error,
  Parameters<typeof api.createProject>[0]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createProject(input),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.projects });
      },
    }),
  });
}

export function useUpdateProject(
  projectId: string,
  options?: {
    success?: string | ((project: Project) => string);
    error?: string | ((error: Error) => string);
  }
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; description?: string; color?: string }) =>
      api.updateProject(projectId, input),
    ...withToast(options ?? {}, {
      onSuccess: (project: Project) => {
        qc.invalidateQueries({ queryKey: queryKeys.projects });
        qc.setQueryData(queryKeys.projects, (prev: { projects: Project[] } | undefined) => {
          if (!prev) return prev;
          return {
            projects: prev.projects.map((p) => (p.id === project.id ? project : p)),
          };
        });
      },
    }),
  });
}

export function useDeleteProject(options?: {
  success?: string | ((data: void, variables: string) => string);
  error?: string | ((error: Error) => string);
  onSuccess?: (data: void, variables: string) => void;
}) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (projectId: string) => api.deleteProject(projectId),
    ...withToast<void, Error, string>(options ?? {}, {
      onSuccess: (_data, projectId) => {
        qc.invalidateQueries({ queryKey: queryKeys.projects });
        qc.removeQueries({ queryKey: queryKeys.reports(projectId) });
        qc.removeQueries({ queryKey: queryKeys.apiKeys(projectId) });
      },
    }),
  });
}

export function useCreateApiKey(
  projectId: string,
  options?: {
    success?: string | ((res: Awaited<ReturnType<typeof api.createApiKey>>) => string);
    error?: string | ((error: Error) => string);
    onSuccess?: (res: Awaited<ReturnType<typeof api.createApiKey>>) => void;
  }
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createApiKey(projectId, name),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.apiKeys(projectId) });
      },
    }),
  });
}

export function useDeleteReport(
  projectId: string,
  options?: {
    success?: string | ((data: void, reportId: string) => string);
    error?: string | ((error: Error) => string);
    onSuccess?: (data: void, reportId: string) => void;
  },
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (reportId: string) => api.deleteReport(projectId, reportId),
    ...withToast<void, Error, string>(options ?? {}, {
      onSuccess: (_data, reportId) => {
        qc.removeQueries({ queryKey: queryKeys.report(projectId, reportId) });
        qc.invalidateQueries({ queryKey: queryKeys.reports(projectId) });
      },
    }),
  });
}

export function useUpdateReport(
  projectId: string,
  reportId: string,
  options?: {
    success?: string | ((report: ReportDetail) => string);
    error?: string | ((error: Error) => string);
  },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      status?: string;
      severity?: string;
      title?: string;
      description?: string;
      assignee?: string;
    }) => api.updateReport(projectId, reportId, patch),
    onMutate: async (patch) => {
      // Optimistically apply the patch to the cached detail so the UI feels
      // instant. If the request fails we roll back from the snapshot.
      const detailKey = queryKeys.report(projectId, reportId);
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<ReportDetail>(detailKey);
      if (previous) {
        const optimistic: ReportDetail = {
          ...previous,
          ...("status" in patch ? { status: patch.status ?? previous.status } : {}),
          ...("severity" in patch ? { severity: patch.severity ?? previous.severity } : {}),
          ...("title" in patch ? { title: patch.title ?? previous.title } : {}),
          ...("description" in patch ? { description: patch.description ?? previous.description } : {}),
          ...("assignee" in patch ? { assignee: patch.assignee ?? previous.assignee } : {}),
        };
        qc.setQueryData(detailKey, optimistic);
      }
      return { previous, detailKey };
    },
    onError: (err, _patch, context) => {
      if (context?.previous) {
        qc.setQueryData(context.detailKey, context.previous);
      }
      if (options?.error) {
        const msg = typeof options.error === "function" ? options.error(err) : options.error;
        if (msg) toast.error(msg);
      }
    },
    onSuccess: (report) => {
      qc.setQueryData(queryKeys.report(projectId, reportId), report);
      qc.invalidateQueries({ queryKey: queryKeys.reports(projectId) });
      if (options?.success) {
        const msg = typeof options.success === "function" ? options.success(report) : options.success;
        if (msg) toast.success(msg);
      }
    },
  });
}

export function useRevokeApiKey(
  projectId: string,
  options?: {
    success?: string | ((res: Awaited<ReturnType<typeof api.revokeApiKey>>) => string);
    error?: string | ((error: Error) => string);
    onSuccess?: (res: Awaited<ReturnType<typeof api.revokeApiKey>>) => void;
  }
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api.revokeApiKey(projectId, keyId),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.apiKeys(projectId) });
      },
    }),
  });
}

export function useLogin() {
  return useMutation({
    // Validates a token by calling listProjects with it set. The caller
    // is responsible for persisting the token via AuthContext.setToken
    // (which also writes to localStorage) and for clearing it on failure.
    // We use a temporary setAdminToken here so the test request can
    // authenticate without coupling the queries layer to AuthContext.
    mutationFn: async (token: string) => {
      setAdminToken(token);
      try {
        await api.listProjects();
      } catch (err) {
        setAdminToken("");
        throw err;
      }
    },
  });
}

// -- Webhooks -----------------------------------------------------------------

export function useWebhooks(projectId: string) {
  return useQuery({
    queryKey: queryKeys.webhooks(projectId),
    queryFn: () => api.listWebhooks(projectId),
    enabled: !!projectId,
  });
}

export function useCreateWebhook(
  projectId: string,
  options?: {
    success?: string | ((hook: Awaited<ReturnType<typeof api.createWebhook>>) => string);
    error?: string | ((err: Error) => string);
    onSuccess?: (hook: Awaited<ReturnType<typeof api.createWebhook>>) => void;
  },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Parameters<typeof api.createWebhook>[1],
    ) => api.createWebhook(projectId, input),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.webhooks(projectId) });
      },
    }),
  });
}

export function useUpdateWebhook(
  projectId: string,
  options?: {
    success?: string | ((hook: Awaited<ReturnType<typeof api.updateWebhook>>) => string);
    error?: string | ((err: Error) => string);
    onSuccess?: (hook: Awaited<ReturnType<typeof api.updateWebhook>>) => void;
  },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      webhookId,
      patch,
    }: {
      webhookId: string;
      patch: Parameters<typeof api.updateWebhook>[2];
    }) => api.updateWebhook(projectId, webhookId, patch),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.webhooks(projectId) });
      },
    }),
  });
}

export function useDeleteWebhook(
  projectId: string,
  options?: {
    success?: string | ((data: void, variables: string) => string);
    error?: string | ((err: Error) => string);
  },
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (webhookId: string) => api.deleteWebhook(projectId, webhookId),
    ...withToast<void, Error, string>(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.webhooks(projectId) });
      },
    }),
  });
}

export function useTestWebhook(
  projectId: string,
  options?: {
    success?: string | ((res: Awaited<ReturnType<typeof api.testWebhook>>) => string);
    error?: string | ((err: Error) => string);
  },
) {
  return useMutation({
    mutationFn: (webhookId: string) => api.testWebhook(projectId, webhookId),
    ...withToast(options ?? {}, {}),
  });
}

// -- MCP tokens --------------------------------------------------------------
//
// MCP tokens are global (admin-scope) credentials. They live outside
// the per-project namespace because a single token can read every
// project, just like the admin token does for the dashboard.

export function useMcpTokens() {
  return useQuery({
    queryKey: queryKeys.mcpTokens,
    queryFn: () => api.listMcpTokens(),
  });
}

export function useCreateMcpToken(options?: {
  success?: string | ((res: Awaited<ReturnType<typeof api.createMcpToken>>) => string);
  error?: string | ((err: Error) => string);
  onSuccess?: (res: Awaited<ReturnType<typeof api.createMcpToken>>) => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createMcpToken(name),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.mcpTokens });
      },
    }),
  });
}

export function useRevokeMcpToken(options?: {
  success?: string | ((res: Awaited<ReturnType<typeof api.revokeMcpToken>>) => string);
  error?: string | ((err: Error) => string);
  onSuccess?: (res: Awaited<ReturnType<typeof api.revokeMcpToken>>) => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeMcpToken(id),
    ...withToast(options ?? {}, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: queryKeys.mcpTokens });
      },
    }),
  });
}